import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faMapMarkerAlt,
  faClipboardList,
  faCalendarCheck,
  faWalking,
  faIdCard,
  faCalendarAlt,
  faArrowLeft,
  faArrowRight
} from "@fortawesome/free-solid-svg-icons";
import CompleteDemo from "pages/dashboard/completeDemo/CompleteDemo";
import UsersVisitList from "pages/dashboard/visit/UsersVisitList";
import PersonalDetails from "./PersonalDetails";
import AssignDistributors from "./AssignedDistributors";
export interface EmployeeData {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobile?: string;
  designation?: string;
  gender?: string;
  city?: string;
  state?: string;
  address?: string;
  postalCode?: string;
  reportingTo?: string;
  status?: string;
  imageUrl?: string;
  completedDemo?: number;
  pendingDemo?: number;
  totalVisits?: number;
  todayVisits?: number;
  employeeCode?: string;
  managerCode?: string;
}

export interface StatsData {
  completedDemo: number;
  pendingDemo: number;
  totalVisits: number;
}

interface NewEmployeeDetailProps {
  data?: EmployeeData;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
  onBack?: () => void;
}

const TABS = [
  { key: "personal", label: "Personal Details", icon: faIdCard },
  { key: "demoList", label: "Demo List", icon: faClipboardList },
  { key: "visitList", label: "Visit List", icon: faWalking },
  // { key: "todayDemo", label: "Today Demo", icon: faCalendarCheck },
  // { key: "dailyVisits", label: "Daily Visits", icon: faCalendarAlt },
  { key: "assignDistributors", label: "Distributors List", icon: faUser },
];

const NewEmployeeDetail: React.FC<NewEmployeeDetailProps> = ({
  data,
  onNext,
  onPrevious,
  hasNext = true,
  hasPrevious = true,
  onBack
}) => {
  const [selectedTab, setSelectedTab] = useState(TABS[0].key);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState<StatsData>({
    completedDemo: data?.completedDemo || 0,
    pendingDemo: data?.pendingDemo || 0,
    totalVisits: data?.totalVisits || 0
  });
  const [todayDemos, setTodayDemos] = useState<any[]>([]);
  const [backgroundDemoLoaded, setBackgroundDemoLoaded] = useState(false);

  // Use effect to simulate data loading
  useEffect(() => {
    if (data) {
      setIsLoading(true);
      // Reset stats when employee changes
      setStats({
        completedDemo: data?.completedDemo || 0,
        pendingDemo: data?.pendingDemo || 0,
        totalVisits: data?.totalVisits || 0
      });
      // Clear today's demos
      setTodayDemos([]);
      // Reset background loading flag
      setBackgroundDemoLoaded(false);
      // Simulate loading data
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [data]);

  // Fallback if no data is provided
  const employee: EmployeeData = data || {};

  // Generate employee full name
  const fullName = [employee.firstName, employee.lastName].filter(Boolean).join(" ") || "N/A";

  // Handler functions to update stats
  const handleCompleteDemoUpdate = useCallback((completedCount: number, pendingCount: number) => {
    setStats(prevStats => ({
      ...prevStats,
      completedDemo: completedCount,
      pendingDemo: pendingCount
    }));
  }, []);

  const handleVisitUpdate = useCallback((visitCount: number) => {
    setStats(prevStats => ({
      ...prevStats,
      totalVisits: visitCount
    }));
  }, []);

  const handleTodayDemoUpdate = useCallback((demos: any[]) => {
    console.log("Received today's demos:", demos.length, demos);
    setTodayDemos(demos);
    setBackgroundDemoLoaded(true);
  }, []);

  // Load demo data in background when needed
  useEffect(() => {
    if (employee.id &&
      (selectedTab === "todayDemo" || !backgroundDemoLoaded)) {
      // Create a hidden component reference to load data
      const demoRef = document.createElement('div');
      demoRef.style.display = 'none';
      document.body.appendChild(demoRef);

      // We'll remove it after a short delay to allow data loading
      const timer = setTimeout(() => {
        document.body.removeChild(demoRef);
      }, 1000);

      return () => {
        clearTimeout(timer);
        if (document.body.contains(demoRef)) {
          document.body.removeChild(demoRef);
        }
      };
    }
  }, [employee.id, selectedTab, backgroundDemoLoaded]);

  // Dynamic tab content based on selected tab
  const renderTabContent = () => {
    switch (selectedTab) {
      case "personal":
        return (
          <div className="bg-white dark:bg-gray-900 
rounded-xl 
shadow-lg dark:shadow-gray-900/40 
p-6 
border border-gray-100 dark:border-gray-800">
            <PersonalDetails employee={employee} />
          </div>
        );
      case "demoList":
        return (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg dark:shadow-gray-900/40 border border-gray-100 dark:border-gray-800 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
              <h3 className="text-white font-bold text-lg flex items-center">
                <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
                Demo List
              </h3>
            </div>
            <div className="max-h-[400px] overflow-auto p-4">
              <CompleteDemo
                viewData={{
                  employeeId: employee.id ?? "",
                  firstName: employee.firstName ?? "",
                  city: employee.city ?? "",
                  completedDemo: stats.completedDemo,
                  pending: stats.pendingDemo
                }}
                isShowHeaders={false}
                onStatsUpdate={handleCompleteDemoUpdate}
                onTodayDemoUpdate={handleTodayDemoUpdate}
              />
            </div>
          </div>
        );
      case "visitList":
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/40 border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-4">
              <h3 className="text-white font-bold text-lg flex items-center">
                <FontAwesomeIcon icon={faWalking} className="mr-2" />
                Visit List
              </h3>
            </div>
            <div className="max-h-[400px] overflow-auto p-4">
              <UsersVisitList
                infoData={{
                  userId: employee.id ?? "",
                  employeeName: employee.firstName ?? "",
                  city: employee.city ?? "",
                  totalVisits: stats.totalVisits
                }}
                onVisitCountUpdate={handleVisitUpdate}
                isHeaderRequired={false}
                showCurrentMonthVisits={false}
              />
            </div>
          </div>
        );
      case "todayDemo":
        return (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg dark:shadow-gray-900/40 p-6 border border-gray-100 dark:border-gray-800">

            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-5 rounded-xl mb-6 flex items-center shadow-md">
              <div className="bg-white/20 p-3 rounded-lg mr-4">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-white text-2xl" />
              </div>
              <div>
                <div className="font-bold text-white text-xl">Today's Schedule</div>
                <div className="text-sm text-blue-100">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
            </div>

            {/* Today's Demo List */}
            <div className="grid gap-4 max-h-[350px] overflow-auto">
              {!backgroundDemoLoaded ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                  <span className="ml-3 text-gray-600 font-medium">Loading today's demos...</span>
                </div>
              ) : todayDemos.length > 0 ? (
                todayDemos.map((demo, i) => (
                  <div key={demo.id} className="border-2 border-gray-200 dark:border-gray-700 
rounded-xl p-5 
hover:shadow-lg dark:hover:shadow-gray-900/50 
hover:border-blue-300 dark:hover:border-blue-500 
transition-all 
bg-gradient-to-br 
from-white to-gray-50 
dark:from-gray-800 dark:to-gray-700">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-lg text-gray-800 dark:text-gray-200">{demo.farmerName}</h4>
                        <p className="text-sm text-gray-400 mt-1 flex items-center">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-blue-500" />
                          {demo.village}, {demo.district}
                        </p>
                      </div>
                      <div className="bg-gradient-to-r from-green-400 to-green-500 text-white text-xs px-3 py-1.5 rounded-full font-semibold shadow-md">
                        {demo.cropName}
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-gray-700 dark:text-gray-200 bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg">
                      {demo.notes || "Demo for " + demo.productName}
                    </div>
                    <div className="mt-3 flex justify-between text-sm">
                      <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-lg font-medium">
                        <strong>Product:</strong> {demo.productName}
                      </span>
                      <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-lg font-medium">
                        <strong>Acres:</strong> {demo.cultivatedAcres}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
                  <div className="text-gray-400 mb-2">
                    <FontAwesomeIcon icon={faCalendarCheck} size="3x" />
                  </div>
                  <p className="text-gray-600 font-medium">No demos scheduled for today</p>
                </div>
              )}
            </div>
          </div>
        );
      case "dailyVisits":
        return (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg dark:shadow-gray-900/40 p-6 border border-gray-100 dark:border-gray-800">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-5 flex items-center shadow-md">
              <div className="bg-white/20 p-3 rounded-lg mr-4">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-white text-2xl" />
              </div>
              <div>
                <div className="font-bold text-white text-xl">Today's Visits</div>
                <div className="text-sm text-purple-100">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
            </div>

            <div className="max-h-[400px] overflow-auto p-4">
              <UsersVisitList
                infoData={{
                  userId: employee.id ?? "",
                  employeeName: employee.firstName ?? "",
                  city: employee.city ?? "",
                  totalVisits: stats.totalVisits
                }}
                onVisitCountUpdate={handleVisitUpdate}
                isHeaderRequired={false}
                filterByToday={true}
              />
            </div>
          </div>
        );
      case "assignDistributors":
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/40 p-6 border border-gray-100 dark:border-gray-700">
            <AssignDistributors employee={employee} />
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 overflow-auto">
      {isLoading ? (
        // Loading Skeleton
        <div className="p-4 space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/3 flex">
                <div className="w-24 h-24 bg-gray-200 rounded-full"></div>
                <div className="ml-4 space-y-3 flex-1">
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="flex gap-2 mt-4">
                    <div className="h-16 bg-gray-200 rounded flex-1"></div>
                    <div className="h-16 bg-gray-200 rounded flex-1"></div>
                  </div>
                </div>
              </div>
              <div className="md:w-1/3 space-y-4 mt-6 md:mt-0">
                <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
              <div className="md:w-1/3 space-y-4 mt-6 md:mt-0">
                <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md h-16 animate-pulse"></div>
          <div className="bg-white rounded-lg shadow-md h-64 animate-pulse"></div>
        </div>
      ) : (
        // Actual Content
        <div className="flex flex-col overflow-auto">



          {/* Profile Section */}
          <div className="bg-gradient-to-br 
from-white to-blue-50 
dark:from-gray-900 dark:to-gray-800
rounded-t-xl 
shadow-lg dark:shadow-gray-900/40
p-6 mx-4 relative 
border border-blue-100 dark:border-gray-800">
            {/* Header with Navigation */}
            <div className="flex justify-between items-start">
              <button
                onClick={onBack}
                className="flex items-center justify-center
  w-12 h-12 -mt-3 -ml-2
  bg-gray-100 hover:bg-gray-200
  dark:bg-gray-800 dark:hover:bg-gray-700
  text-gray-700 dark:text-gray-200
  rounded-full shadow-sm
  transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
              </button>

              {/* Employee Profile */}
              <div className="flex items-start flex-1">
                <div className="relative">
                  <div className=" ml-3 w-28 h-28 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-100 to-blue-200 border-4 border-white shadow-xl ring-2 ring-blue-100">
                    {employee.imageUrl ? (
                      <img
                        src={employee.imageUrl}
                        alt={fullName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 text-white">
                        <FontAwesomeIcon icon={faUser} size="3x" />
                      </div>
                    )}
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-4 border-white shadow-lg ${employee.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                    }`}></div>
                </div>
                <div className="ml-6 flex-1">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{fullName}</h2>
                  <p className="text-blue-600 font-semibold text-lg">{employee.designation ?? "N/A"}</p>
                  <p className="text-blue-600 font-semibold text-lg mb-4">{employee.employeeCode ?? "N/A"}</p>

                </div>
              </div>

              {/* Navigation Buttons - Moved Here */}
              <div className="flex flex-row gap-2 ml-4">

                <button
                  onClick={onPrevious}
                  disabled={!hasPrevious}
                  className={`flex items-center px-4 py-2 text-sm rounded-lg transition-all shadow-md ${hasPrevious
                    ? 'bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-300 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed border border-gray-200 dark:border-gray-700'
                    }`}
                  title="Previous Employee"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  Previous
                </button>
                <button
                  onClick={onNext}
                  disabled={!hasNext}
                  className={`flex items-center px-4 py-2 text-sm rounded-lg transition-all shadow-md ${hasNext
                    ? 'bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-300 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed border border-gray-200 dark:border-gray-700'
                    }`}
                  title="Next Employee"
                >
                  Next
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </button>
              </div>
            </div>

          </div>

          {/* Tabs Navigation - Fixed position */}
          <div className="px-4 sticky top-0 z-10 ">
            <div className="bg-white dark:bg-gray-900 rounded-b-xl shadow-lg dark:shadow-gray-900/40 p-2 flex overflow-x-auto gap-2 border border-gray-100 dark:border-gray-800">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setSelectedTab(tab.key)}
                  className={`flex items-center px-5 py-3 rounded-lg transition-all duration-300 whitespace-nowrap font-medium ${selectedTab === tab.key
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105 transform'
                    : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600 hover:shadow-md'
                    }`}
                >
                  <FontAwesomeIcon icon={tab.icon} className={`mr-2 ${selectedTab === tab.key ? 'text-white' : ''}`} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content - Scrollable */}
          <div className="flex-1 px-4 overflow-auto">
            {renderTabContent()}

            {/* Hidden component to ensure today's demos are loaded even when not on demo tab */}
            {!backgroundDemoLoaded && employee.id && (
              <div style={{ display: 'none' }}>
                <CompleteDemo
                  viewData={{
                    employeeId: employee.id ?? "",
                    firstName: employee.firstName ?? "",
                    city: employee.city ?? "",
                    completedDemo: stats.completedDemo,
                    pending: stats.pendingDemo
                  }}
                  isShowHeaders={false}
                  onStatsUpdate={handleCompleteDemoUpdate}
                  onTodayDemoUpdate={handleTodayDemoUpdate}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewEmployeeDetail;